/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useImpostazioni, urlImmagine } from '../hooks/useImpostazioni';
import NotificheBell from './NotificheBell';
import { useViewport } from '../hooks/useViewport';
import { LayoutDashboard, Users, Wrench, Package, Hammer, BarChart3, LogOut, Menu, X, Boxes, ShieldAlert , Settings, Truck, Receipt, MessageSquarePlus, CalendarDays } from 'lucide-react';

const ACCENT = '#8B5CF6';

const NAV_ITEMS = [
  { to: '/dashboard', label: 'Dashboard',  icon: LayoutDashboard },
  { to: '/utenti',    label: 'Utenti',     icon: Users },
  { to: '/interventi',label: 'Interventi', icon: Wrench },
  { to: '/calendario', label: 'Calendario', icon: CalendarDays },
  { to: '/materiali', label: 'Materiali',  icon: Package },
  { to: '/fornitori', label: 'Fornitori',  icon: Truck },
  { to: '/strumenti', label: 'Strumenti',  icon: Hammer },
  { to: '/prodotti',  label: 'Prodotti',   icon: Boxes },
  { to: '/fatturazione', label: 'Fatturazione', icon: Receipt },
  { to: '/report',    label: 'Report',     icon: BarChart3 },
  { to: '/richieste-privacy', label: 'Richieste privacy', icon: ShieldAlert },
  { to: '/segnalazioni', label: 'Segnalazioni', icon: MessageSquarePlus },
  { to: '/impostazioni', label: 'Impostazioni', icon: Settings },
];

export default function AdminLayout({ children, title }) {
  const { user, logout } = useAuth();
  const impostazioni = useImpostazioni();
  const logo = urlImmagine(impostazioni.app_logo_url);
  const location = useLocation();
  const { isCompact } = useViewport();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Su desktop la sidebar è sempre visibile; su mobile/tablet scorre da sinistra.
  const sidebarVisible = !isCompact || mobileOpen;

  return (
    <div style={styles.wrapper}>
      <aside style={{
        ...styles.sidebar,
        ...(isCompact ? styles.sidebarCompact : {}),
        transform: sidebarVisible ? 'translateX(0)' : 'translateX(-100%)',
      }}>
        <div style={styles.brand}>
          {logo
              ? <img src={logo} alt="" style={styles.brandLogo} />
              : <div style={{ ...styles.brandDot, backgroundColor: ACCENT }} />}
          <div>
            <div style={styles.brandTitle}>{impostazioni.app_nome}</div>
            <div style={styles.brandSubtitle}>Admin</div>
          </div>
        </div>

        <nav style={styles.nav}>
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => {
            const active = location.pathname.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileOpen(false)}
                style={{
                  ...styles.navLink,
                  ...(active ? { backgroundColor: '#F3E8FF', color: ACCENT, fontWeight: 600 } : {}),
                }}
              >
                <Icon size={18} /> {label}
              </Link>
            );
          })}
        </nav>

        <div style={styles.userBox}>
          <div style={styles.userName}>{user?.nome} {user?.cognome}</div>
          <div style={styles.userEmail}>{user?.email}</div>
          <button onClick={logout} style={styles.logoutBtn}>
            <LogOut size={16} /> Esci
          </button>
          <Link to="/privacy" style={styles.privacyLink}>Informativa Privacy</Link>
        </div>
      </aside>

      <div style={{ ...styles.main, ...(isCompact ? styles.mainCompact : {}) }}>
        <header style={styles.header}>
          {isCompact && (
            <button onClick={() => setMobileOpen(!mobileOpen)} style={styles.menuBtn} aria-label="Menu">
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          )}
          <h1 style={{ ...styles.pageTitle, ...(isCompact ? styles.pageTitleCompact : {}) }}>{title}</h1>
          <div style={{ marginLeft: 'auto' }}>
            <NotificheBell />
          </div>
        </header>

        <div style={{ ...styles.content, ...(isCompact ? styles.contentCompact : {}) }}>{children}</div>
      </div>

      {isCompact && mobileOpen && (
        <div style={styles.overlay} onClick={() => setMobileOpen(false)} />
      )}
    </div>
  );
}

const styles = {
  wrapper: { display: 'flex', minHeight: '100vh', backgroundColor: '#F9FAFB' },
  sidebar: { width: '240px', backgroundColor: 'white', borderRight: '1px solid #E5E7EB', padding: '1.5rem 1rem', display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 40, transition: 'transform 0.2s ease' },
  sidebarCompact: { boxShadow: '2px 0 12px rgba(0,0,0,0.12)' },
  brand: { display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '2rem', paddingLeft: '0.5rem' },
  brandDot: { width: '12px', height: '12px', borderRadius: '4px' },
  brandLogo: { width: '28px', height: '28px', objectFit: 'contain', flexShrink: 0 },
  brandTitle: { fontWeight: 700, fontSize: '0.95rem', color: '#111827' },
  brandSubtitle: { fontSize: '0.75rem', color: '#6B7280', marginTop: '2px' },
  nav: { display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1 },
  navLink: { display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.7rem 0.75rem', borderRadius: '8px', color: '#374151', textDecoration: 'none', fontSize: '0.92rem' },
  userBox: { borderTop: '1px solid #E5E7EB', paddingTop: '1rem', marginTop: '1rem' },
  userName: { fontWeight: 600, fontSize: '0.9rem', color: '#111827' },
  userEmail: { fontSize: '0.78rem', color: '#6B7280', marginTop: '2px', wordBreak: 'break-all' },
  logoutBtn: { marginTop: '0.75rem', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.6rem', backgroundColor: '#FEE2E2', color: '#991B1B', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 },
  privacyLink: { display: 'block', textAlign: 'center', marginTop: '0.6rem', color: '#9CA3AF', fontSize: '0.75rem', textDecoration: 'none' },
  main: { flex: 1, marginLeft: '240px', display: 'flex', flexDirection: 'column', minWidth: 0 },
  mainCompact: { marginLeft: 0 },
  header: { backgroundColor: 'white', borderBottom: '1px solid #E5E7EB', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', position: 'sticky', top: 0, zIndex: 20 },
  menuBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', color: '#374151', padding: '0.35rem', minWidth: '40px', minHeight: '40px' },
  pageTitle: { margin: 0, fontSize: '1.35rem', fontWeight: 700, color: '#111827' },
  pageTitleCompact: { fontSize: '1.1rem' },
  content: { padding: '1.5rem', flex: 1, minWidth: 0 },
  contentCompact: { padding: '1rem' },
  overlay: { position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 30 },
};
